import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Modal, ModalService } from 'src/app/project/components/modal/modal.service';
import { logEventInGTAG } from '../../services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from '../../services/analytics/google-analytics.consts';
import { TPoint } from '../points/points.model';
import { PointExportDetailedListService } from './point-export-detailed-list.service';
import { PointExportLocalService } from './point-export-local.service';
import { PointExportPdfService } from './point-export-pdf.service';
import { PointExportSimpleListService } from './point-export-simple-list.service';
import { PointExportSitePlanService } from './point-export-site-plan.service';
import { PointExportWordService } from './point-export-word.service';
import { PointExportZipService } from './point-export-zip.service';
import { TExportCustomField } from './utils/export-custom-field';
import { TExportData } from './utils/export-data';
import { EExportTypeName } from './utils/export-type-names-enum';
import { sendExportGoogleEvent } from './utils/send-export-google-event';

@Injectable({
  providedIn: 'root',
})
export class PointExportRequestService {
  private modal: Modal = this.modalService.getModal();

  constructor(
    private modalService: ModalService,
    private pointExportZipService: PointExportZipService,
    private pointExportWordService: PointExportWordService,
    private pointExportSitePlanService: PointExportSitePlanService,
    private pointExportSimpleListService: PointExportSimpleListService,
    private pointExportDetailedListService: PointExportDetailedListService,
    private pointExportPdfService: PointExportPdfService,
    private pointExportLocalService: PointExportLocalService,
  ) {}

  export(
    exportData: TExportData,
    point: TPoint,
    customFields: TExportCustomField[],
  ): Observable<Response> {
    this.sendExportGoogleEvent(exportData);

    switch (exportData.selected.value) {
      case EExportTypeName.ZIP:
        return this.exportZip(exportData, customFields);
      case EExportTypeName.CSV:
        this.pointExportLocalService.exportLocally('csv');

        return of(null);
      case EExportTypeName.XLSX:
        this.pointExportLocalService.exportLocally('xlsx');

        return of(null);
      case EExportTypeName.PDF:
      case EExportTypeName.PDF_ZIP:
        return this.exportPdf(exportData, customFields);
      case EExportTypeName.WORD:
        return this.exportWord(point, customFields, exportData);
      case EExportTypeName.SITE_PLAN:
        return this.pointExportSitePlanService.exportSitePlan({
          exportedPoint: point,
          data: this.modal.data,
          exportData: exportData,
        });
      case EExportTypeName.SIMPLE_LIST:
        return this.exportSimpleList(point, exportData);
      case EExportTypeName.WORD_SIMPLE:
        return this.pointExportWordService.exportOverviewWordSimpleList({
          exportData: exportData,
        });
      case EExportTypeName.DETAILED_LIST:
        return this.exportDetailedList(point, customFields, exportData);
    }
  }

  private exportDetailedList(
    point: TPoint,
    customFields: TExportCustomField[],
    exportData: TExportData,
  ): Observable<Response> {
    const overviewExportParameters = {
      exportedCustomFields: customFields,
      exportData: exportData,
    };

    if (this.modal.data.workspaceId) {
      return this.pointExportDetailedListService.exportDetailedList({
        ...overviewExportParameters,
        exportedPoint: point,
        data: this.modal.data,
      });
    } else {
      return this.pointExportDetailedListService.exportOverviewDetailedList(
        overviewExportParameters,
      );
    }
  }

  private exportSimpleList(point: TPoint, exportData: TExportData): Observable<Response> {
    const overviewExportParameters = {
      exportData: exportData,
    };

    if (this.modal.data.workspaceId) {
      return this.pointExportSimpleListService.exportSimpleList({
        ...overviewExportParameters,
        exportedPoint: point,
        data: this.modal.data,
      });
    } else {
      return this.pointExportSimpleListService.exportOverviewSimpleList(overviewExportParameters);
    }
  }

  private exportWord(
    point: TPoint,
    customFields: TExportCustomField[],
    exportData: TExportData,
  ): Observable<Response> {
    return this.pointExportWordService.exportWord({
      exportedPoint: point,
      data: this.modal.data,
      exportedCustomFields: customFields,
      exportData: exportData,
    });
  }

  private exportPdf(
    exportData: TExportData,
    customFields: TExportCustomField[],
  ): Observable<Response> {
    if (exportData.showImages) {
      logEventInGTAG(EGoogleEventSite.SITE__EXPORT__IMAGES, {
        source: EGoogleEventCategory.SITE,
        format: exportData.imagesSize,
      });
    }

    return this.pointExportPdfService.exportPdf(customFields, exportData);
  }

  private exportZip(
    exportData: TExportData,
    customFields: TExportCustomField[],
  ): Observable<Response> {
    if (exportData.showImages) {
      logEventInGTAG(EGoogleEventSite.SITE__EXPORT__IMAGES, {
        source: EGoogleEventCategory.SITE,
        format: exportData.imagesSize,
      });
    }

    return this.pointExportZipService.exportZip({
      _id: null,
      isPdf: false,
      data: this.modal.data,
      exportData: exportData,
      exportedCustomFields: customFields,
    });
  }

  private sendExportGoogleEvent(exportData: TExportData): void {
    sendExportGoogleEvent(exportData.selected.value, this.modal.data.pointExport);
  }
}
